// Third party
import React, { Component } from 'react';
import { connect } from 'react-redux';
import classNames from 'classnames';

// Components
import { QuickSearch } from '../common';
import EditBenchmarkHolding from './EditBenchmarkHolding';

// Utils
import { QUICK_SEARCH_TYPE, QUICK_SEARCH_REDUCER_NAME } from '../../../redux/modules/quick-search';
import { formatMessage } from '../../../utils/i18n';
import { showError } from '../../../utils/errors';
import { getTotalBenchmarkAllocation, benchmarkIsFull } from '../../../utils/comparison/benchmarks';
import HoldingsEditor from '../holdings-editor/HoldingsEditor';
import notification from '../../../utils/notification';
import { HOLDINGS_EDITOR_TYPE } from '../../../utils/constants';

// Actions
import { ReduxState } from '../../../redux/modules/types';
import {
  addBenchmarkToPortfolioById,
  removeBenchmarkHoldingById,
  changeHoldingWeight,
  cancelBenchmarkEditing,
  allocateEvenly,
  allocateRemaining,
  setBenchmarkPortfolioName,
} from '../../../redux/modules/edit-benchmark';
import BenchmarkPortfolio from '@/types/benchmark-portfolio';
import ComparisonItem from '@/types/comparison-item';
import QuickSearchItem from '@/types/quick-search-item';
import { QuickSearchV2 } from '../search/quick-search-v2';

// Legacy Component Support
import withLegacyVersion from '../hocs/withLegacyVersion';
import LegacyEditBenchmark from './LegacyEditBenchmark';

type DispatchProps = {
  addBenchmarkToPortfolioById: (id: string) => Promise<void>;
  removeBenchmarkHoldingById: (holdingId: string) => void;
  changeHoldingWeight: (holdingId: string, weight: number) => void;
  cancelBenchmarkEditing: () => void;
  allocateRemaining: () => void;
  allocateEvenly: () => void;
  setBenchmarkPortfolioName: (name: string) => void;
};

type StateProps = {
  editedBenchmark?: BenchmarkPortfolio;
};

type Props = DispatchProps &
  StateProps & {
    className?: string;
    onSave: (benchmarkPortfolio: BenchmarkPortfolio) => Promise<void>;
    onCancel?: (benchmarkPortfolio: BenchmarkPortfolio) => void;
    readOnly?: boolean;
    showCurrencies: boolean;
    item?: ComparisonItem;
  };

class EditBenchmark extends Component<Props> {
  onSave = () => {
    const { editedBenchmark } = this.props;

    if (!editedBenchmark) {
      return Promise.reject();
    }

    return this.props
      .onSave(editedBenchmark)
      .then(() => {
        this.props.cancelBenchmarkEditing();
        notification.success(formatMessage('HoldingsEditor.Saved.BENCHMARK'));
      })
      .catch(showError);
  };

  onCancel = () => {
    const { editedBenchmark, onCancel } = this.props;

    if (!editedBenchmark) {
      return;
    }

    this.props.cancelBenchmarkEditing();

    if (onCancel) {
      onCancel(editedBenchmark);
    }
  };

  onAddItem = (itemId: string) => {
    this.props.addBenchmarkToPortfolioById(itemId).catch(showError);
  };

  onWeightChange = (holdingId: string, weight: number) => {
    this.props.changeHoldingWeight(holdingId, weight);
  };

  onRemoveHolding = (holdingId: string) => {
    this.props.removeBenchmarkHoldingById(holdingId);
  };

  saveIsDisabled = () => {
    const { editedBenchmark } = this.props;

    return !editedBenchmark || !benchmarkIsFull(editedBenchmark.holdings) || !editedBenchmark.name;
  };

  renderHoldings = () => {
    const { editedBenchmark, readOnly, showCurrencies } = this.props;

    if (!editedBenchmark) {
      return [];
    }

    return editedBenchmark.holdings.map(holding => (
      <EditBenchmarkHolding
        readOnly={readOnly}
        holdingCount={editedBenchmark.holdings.length}
        key={holding.id}
        holding={holding}
        onRemoveHolding={this.onRemoveHolding}
        onWeightChange={this.onWeightChange}
        showCurrencies={showCurrencies}
      />
    ));
  };

  renderQuickSearch = () => (
    <QuickSearchV2
      targetReducer={QUICK_SEARCH_REDUCER_NAME.EDIT_BENCHMARK}
      allowedTypes={[QUICK_SEARCH_TYPE.BENCHMARKS]}
      className="edit-benchmark-quick-search"
      onAddBenchmarkById={(itemId: string) => this.onAddItem(itemId)}
      options={{
        withAdvancedScreener: false,
        allowedTypes: [QUICK_SEARCH_TYPE.BENCHMARKS],
        excludeSubtypes: true,
      }}
    />
  );

  render() {
    const { editedBenchmark, className } = this.props;

    if (!editedBenchmark) {
      return null;
    }

    const { name, holdings } = editedBenchmark;

    return (
      <HoldingsEditor
        className={classNames('edit-benchmark', className)}
        type={HOLDINGS_EDITOR_TYPE.BENCHMARK}
        name={name}
        holdings={holdings}
        quickSearchComponent={this.renderQuickSearch()}
        holdingsComponent={this.renderHoldings()}
        totalAllocated={getTotalBenchmarkAllocation(holdings)}
        onAllocateEvenly={this.props.allocateEvenly}
        onAllocateRemaining={this.props.allocateRemaining}
        onSave={this.onSave}
        onCancel={this.onCancel}
        setPortfolioName={this.props.setBenchmarkPortfolioName}
        saveIsDisabled={this.saveIsDisabled()}
        hasSearchInputOnTop
      />
    );
  }
}

const mapStateToProps = (state: ReduxState) => ({
  editedBenchmark: state.editBenchmarks.editedBenchmark,
});

export default withLegacyVersion(
  connect(mapStateToProps, {
    addBenchmarkToPortfolioById,
    removeBenchmarkHoldingById,
    changeHoldingWeight,
    cancelBenchmarkEditing,
    allocateEvenly,
    allocateRemaining,
    setBenchmarkPortfolioName,
  })(EditBenchmark),
  LegacyEditBenchmark,
);
